import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { HttpStatusCode } from 'src/Common/Models/UATConstants';
import { IOpportunity, IReferral, ITpidResponse } from 'src/Common/RTK/interface/ViewFolder';
import { IGPSInvestments } from 'src/manageAction/interfaces/GPS';
import {
  ICommentItem,
  IGetUserAlias,
  ILeftTabData,
  IPartnerRegionAreaFromCountry,
  IPersonalDetails,
  ITechnicalFeedback,
  IUpdatedAttachment,
} from 'src/manageAction/interfaces/ManageAction';
import { IWorkItem } from 'src/manageAction/interfaces/WorkItem';
import {
  UpdateActionApiStatus,
} from 'src/manageAction/utils/manageActionConstants';
import { Ahpm } from 'src/Models/Ahpm';
import { AOAICapacity } from 'src/Models/AOAICapacity';
import { Apex } from 'src/Models/Apex';
import { Attachment } from 'src/Models/Attachment';
import { CAE } from 'src/Models/CAE';
import { Compete } from 'src/Models/Compete';
import { CoSell } from 'src/Models/CoSell';
import { CSG } from 'src/Models/CSG';
import { CSR } from 'src/Models/CSR';
import { ECIFPreApproval } from 'src/Models/ECIFPreApproval';
import { GlobalTeam } from 'src/Models/GlobalTeam';
import { WorkItemUpdate } from 'src/Models/HistoryItem';
import { IAT } from 'src/Models/IAT';
import { MACC } from 'src/Models/Macc';
import { MCAPSMaaS } from 'src/Models/MCAPSMaaS';
import { mwHub } from 'src/Models/Nominations';
import { RedButton } from 'src/Models/RedButton';
import { Related } from 'src/Models/Related';
import { BasicDetails, CommonSharedData, RAID, UpdateWorkitem } from 'src/Models/UpdateWorkitem';
import { VSUQBC } from 'src/Models/VSUQBC';
import { rtkApi } from '../../../Common/RTK/api/rtkApi';
import { getActionDataApi } from '../services/getActionApi';
import { FeatureList } from 'src/utils/TenantCheckerUtils';
import { GPSTask } from 'src/GPSInvestments/Models/GPSTasks';
import { PMYTDetails } from 'src/Models/PMYTDetails';
import { IDropdownOption } from '@fluentui/react';
import { GSIDetails } from 'src/manageAction/interfaces/GSI';

// As fluent ui 9 accordion Component is removing all child component from DOM local states are vanished on toggle of accordion.
// So we are using redux to maintain the local states.Once accordion fixed we can remove store states and use local states.
export interface ITemporaryLocalStates {
  currentRemoteUrl: string;
  gps_StrategyLeadDisplayName_Frontend: string;
  gps_PIRManagerDisplayName_Frontend: string;
  gps_CELAPIRStakeholderDisplayName_Frontend: string;
  gps_CompliancePIRStakeholderDisplayName_Frontend: string;
  gps_OperationsPIRStakeholderDisplayName_Frontend: string;
  gps_FinancePIRStakeholderDisplayName_Frontend: string;
  gps_CorpAccountingPIRStakeholderDisplayName_Frontend: string;
}

export interface IGetActionDataSlice extends ITemporaryLocalStates {
  actionDetails: IWorkItem;
  isAuthorized: boolean;
  isSplitView: boolean;
  history: WorkItemUpdate[];
  isHistoryLoading: boolean;
  InitActionData: object;
  UpdatedActionData: UpdateWorkitem;
  isFormDirty: boolean;
  workItemSubscriptions: boolean;
  isFollowing: boolean;
  technicalFeedbackList: ITechnicalFeedback[];
  commentList: ICommentItem[];
  comment: ICommentItem;
  adoTags: string[];
  updateComment: any;
  deleteComments: any;
  commonSharedData: CommonSharedData;
  personalDetails: IPersonalDetails;
  managerDetails: string;
  getAlias: IGetUserAlias;
  getEmail: any;
  opportunityDetails: IOpportunity;
  referralDetails: IReferral[];
  tpidDetails: ITpidResponse;
  opportunityDetailData: {
    opportunityID: string;
    referral: string[];
    tpid: string;
  };
  isTaskInformationDirty: boolean;
  tasksInformation: GPSTask[];
  partnerRegionAreaFromCountry: IPartnerRegionAreaFromCountry[];
  isFetchingOpportunityDetails: boolean;
  //Error and loading flags
  isUpdateActionCallInProgress: boolean;
  isConflictDialogHidden: boolean;
  isGetActionCallInProgress: boolean;
  updateActionApiStatus: UpdateActionApiStatus;
  dwrRequiredFields: string[];
  editableFeatures: FeatureList;
  getClickedStage: string;
  selectedActionId: string;
  rbacFeatures: string[];
  isMaximized: boolean;
  isSplitScreen: boolean;
  deDupeData?: any;
  deDupeDataLoading?: boolean;
  workScopeItems: Dropdown[];
  hasExcelValidationError: boolean;
  hasMSRAttachmentValidationError: boolean;
}

//Temporary local states object
const tempLocalStates = {
  currentRemoteUrl: '',
};

const initialState = {
  actionDetails: {},
  isAuthorized: false,
  isSplitView: false,
  isHistoryLoading: false,
  InitActionData: {},
  UpdatedActionData: {},
  isFormDirty: false,
  workItemSubscriptions: false,
  isFollowing: false,
  commonSharedData: {},
  opportunityDetails: {},
  referralDetails: {},
  tpidDetails: {},
  opportunityDetailData: {},
  permissionsVersion: 0, // Add a counter to track permission refreshes
  partnerRegionAreaFromCountry: {},
  //Error and loading flags
  isFetchingOpportunityDetails: false,
  isUpdateActionCallInProgress: false,
  isConflictDialogHidden: true,
  isGetActionCallInProgress: false,
  updateActionApiStatus: UpdateActionApiStatus.None,
  dwrRequiredFields: [],
  editableFeatures: {
    isleftHandComponentDisabled: false,
    isRightHandSectionDisabled: false,
    isUnAuthorized: false,
    isTabSectionDisabled: false,
    isStatusUpdate: false,
    isClosureUpdate: false,
    isGPSBPODisabled: false,
  },
  selectedActionId: '',
  hasExcelValidationError: false,
  hasMSRAttachmentValidationError: false,
  ...tempLocalStates,
  isTaskInformationDirty: false,
  tasksInformation: [],
  rbacFeatures: [],
  isMaximized: false,
  isSplitScreen: false,
  deDupeData: [],
  deDupeDataLoading: false,
} as unknown as IGetActionDataSlice;

export const manageActionSlice = createSlice({
  name: 'manageActionData',
  initialState,
  reducers: {
    refreshPermissions: (state) => {
      // Increment the permissions version counter to force a re-render
      // in components that are listening for permission changes
      state.permissionsVersion = (state.permissionsVersion || 0) + 1;
    },
    initFormDirty: (state, action: PayloadAction<boolean>) => {
      state.isFormDirty = action.payload;
    },
    commitUpdatedActionData: (state) => {
      state.InitActionData = _.cloneDeep(state.UpdatedActionData);
      state.isFormDirty = false;
    },
    initComments: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          comments: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          comments: _.cloneDeep(action.payload),
        },
      };
    },
    initActionBasicDetails: (state, action: PayloadAction<BasicDetails>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          actionDetails: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          actionDetails: _.cloneDeep(action.payload),
        },
      };
    },
    selectedAssignedTeam: (state, action: PayloadAction) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          assignedTeamValue: action.payload,
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          assignedTeamValue: action.payload,
        },
      };
    },
    initRaidDetails: (state, action: PayloadAction<RAID>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          raidData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          raidData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionCSGDetails: (state, action: PayloadAction<CSG>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          csgData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          csgData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionVsuQbcDetails: (state, action: PayloadAction<VSUQBC>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          vsuQbcData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          vsuQbcData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionIATDetails: (state, action: PayloadAction<IAT>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          iatData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          iatData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionMaccDetails: (state, action: PayloadAction<MACC>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          maccData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maccData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionRedButtonDetails: (state, action: PayloadAction<RedButton>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          redButtonData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          redButtonData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionCoSellDetails: (state, action: PayloadAction<CoSell>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          CoSellDetails: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          CoSellDetails: _.cloneDeep(action.payload),
        },
      };
    },

    updateActionCosellDetails: (state, action: PayloadAction<CoSell>) => {
      // Check if this is initial MSX data load
      const initCoSell = state.InitActionData?.CoSellDetails;
      const isInitialMSXLoad =
        initCoSell &&
        // Init data missing MSX-exclusive fields AND payload is populating them
        ((initCoSell.CoSellInitiatedBy === undefined &&
          action.payload.CoSellInitiatedBy !== undefined) ||
          (initCoSell.segment === undefined && action.payload.segment !== undefined) ||
          (initCoSell.subSegment === undefined && action.payload.subSegment !== undefined) ||
          (initCoSell.CoSellPartnerCountry === undefined &&
            action.payload.CoSellPartnerCountry !== undefined) ||
          (initCoSell.CoSellMCEMSalesStage === undefined &&
            action.payload.CoSellMCEMSalesStage !== undefined));

      const reduxState = {
        ...state,
        // If initial MSX load, sync both Init and Updated
        InitActionData: isInitialMSXLoad
          ? {
              ...state.InitActionData,
              CoSellDetails: _.cloneDeep(action.payload),
            }
          : state.InitActionData,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          CoSellDetails: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateActionGPSDetails: (state, action: PayloadAction<IGPSInvestments>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gpsData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    //Partner Marketing Yield Tracker Update action
    initActionPMYTDetails: (state, action: PayloadAction<PMYTDetails>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          pmytData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          pmytData: _.cloneDeep(action.payload),
        },
      };
    },
    updateActionPMYTDetails: (state, action: PayloadAction<PMYTDetails>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          pmytData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },

    initActionGTTDetails: (state, action: PayloadAction<CSR>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          gttData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gttData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionGlobalTeamDetails: (state, action: PayloadAction<GlobalTeam>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          globalTeamData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          globalTeamData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionCaeDetails: (state, action: PayloadAction<CAE>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          caeData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          caeData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionCAPSLDetails: (state, action: PayloadAction) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          capslData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          capslData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionAPEXDetails: (state, action: PayloadAction<Apex>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          apexData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionAOAIDetails: (state, action: PayloadAction<AOAICapacity>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          aoaiCapacityData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionECIFDetails: (state, action: PayloadAction<ECIFPreApproval>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ecifData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionGPSInvDetails: (state, action: PayloadAction<IGPSInvestments>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          gpsData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gpsData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionMAASDetails: (state, action: PayloadAction<MCAPSMaaS>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          maaSData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maaSData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionAHPMDetails: (state, action: PayloadAction<Ahpm>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ahpmData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionActionNominationsDetails: (state, action: PayloadAction<mwHub>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          mwHubData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          mwHubData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionRelatedDetails: (state, action: PayloadAction<Related>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          relatedData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          relatedData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionAttachmentDetails: (state, action: PayloadAction<Attachment>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          attachments: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          attachments: _.cloneDeep(action.payload),
        },
      };
    },
    initActionTagsDetails: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          tags: _.cloneDeep(action.payload.toString()),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          tags: _.cloneDeep(action.payload.toString()),
        },
      };
    },
    initActionCompeteDetails: (state, action: PayloadAction<Compete>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          competeData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          competeData: _.cloneDeep(action.payload),
        },
      };
    },
    initActionLeftTabDetails: (state, action: PayloadAction<ILeftTabData>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          leftTabData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: _.cloneDeep(action.payload),
        },
      };
    },
    updateActionLeftTabDetails: (state, action: PayloadAction<ILeftTabData>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateActionAttachmentDetails: (state, action: PayloadAction<IUpdatedAttachment>) => {
      let reduxState;
      if (
        action.payload.deletedAttachments.length > 0 ||
        action.payload.newAttachments.length > 0
      ) {
        reduxState = {
          ...state,
          InitActionData: {
            ...state.InitActionData,
          },
          UpdatedActionData: {
            ...state.UpdatedActionData,
            attachments: {
              items: _.cloneDeep(action.payload.updatedAttachments),
              newItems: _.cloneDeep(action.payload.newAttachments),
              deleteItems: _.cloneDeep(action.payload.deletedAttachments),
            },
          },
        };
      } else {
        reduxState = {
          ...state,
          InitActionData: {
            ...state.InitActionData,
          },
          UpdatedActionData: {
            ...state.InitActionData,
          },
        };
      }

      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },

    updateActionTagsDetails: (state, action: PayloadAction<string>) => {
      const tagsData = action.payload.toString();
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          tags: tagsData,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateActionRelatedDetails: (state, action: PayloadAction<Related>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          relatedData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },

    updateActionBasicDetails: (state, action: PayloadAction<BasicDetails>) => {
      const initDetails = state.InitActionData?.actionDetails;

      // Check if opportunityDetails is being populated for the first time
      // This happens when the API response comes back with solutionArea data
      const isInitialAsyncLoad =
        initDetails &&
        action.payload.opportunityDetails?.solutionArea &&
        // Check if solutionArea is different AND this looks like an API response (not user edit)
        initDetails.opportunityDetails?.solutionArea !==
          action.payload.opportunityDetails.solutionArea &&
        // Additional check: if it's initial load, InitActionData should match UpdatedActionData before this update
        _.isEqual(state.InitActionData, state.UpdatedActionData) &&
        !state.isFormDirty;

      const reduxState = {
        ...state,
        InitActionData: isInitialAsyncLoad
          ? {
              ...state.InitActionData,
              actionDetails: _.cloneDeep(action.payload),
            }
          : state.InitActionData,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          actionDetails: action.payload,
        },
      };

      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;

      if (isFormDirty) {
        printDifference(reduxState);
      }

      return reduxState;
    },
    getWorkItemComments: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          commentsData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          commentsData: _.cloneDeep(action.payload),
        },
      };
    },
    updateActionNewCommentDetails: (state, action: PayloadAction<ICommentItem>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          comments: action.payload.content,
        },
      };

      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateActionAOAIDetails: (state, action: PayloadAction<AOAICapacity>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    commonSharedDataDetails: (state, action: PayloadAction<CommonSharedData>) => {
      return {
        ...state,
        ...state.commonSharedData,
        commonSharedData: _.cloneDeep(action.payload),
      };
    },

    PersonDetails: (state, action: PayloadAction<IPersonalDetails>) => {
      state.personalDetails = action.payload;
    },
    GetAlias: (state, action: PayloadAction<IGetUserAlias>) => {
      state.getAlias = action.payload;
    },
    GetEmail: (state, action: PayloadAction<any>) => {
      state.getEmail = action.payload;
    },
    updateOpportunityDetails: (
      state,
      action: PayloadAction<{
        opportunityID: string;
        referral: string[];
        tpid: string;
      }>
    ) => {
      state.opportunityDetailData = action.payload;
    },
    setIsConflictDialogHidden: (state, action: PayloadAction<boolean>) => {
      state.isConflictDialogHidden = action.payload;
    },
    setIsFetchingOpportunityDetails: (state, action: PayloadAction<boolean>) => {
      state.isFetchingOpportunityDetails = action.payload;
    },
    setUpdateActionApiStatus: (state, action: PayloadAction<UpdateActionApiStatus>) => {
      state.updateActionApiStatus = action.payload;
    },
    setDWRRequiredFields: (state, action: PayloadAction<string[]>) => {
      state.dwrRequiredFields = action.payload;
    },
    //Temporary local states updates start
    setCurrentRemoteUrl: (state, action: PayloadAction<string>) => {
      state.currentRemoteUrl = action.payload;
    },
    //Temporary local states updates end
    setEditableFeatureList: (state, action: PayloadAction<FeatureList>) => {
      state.editableFeatures = action.payload;
    },
    //Temporary local states updates end
    setClickableStage: (state, action: PayloadAction<string>) => {
      state.getClickedStage = action.payload;
    },
    // setEditableAccordion: (state, action: PayloadAction<boolean>) => {
    //   state.editableFeatures.isleftHandComponentDisabled = action.payload;
    // },
    setSelectedActionId: (state, action: PayloadAction<string>) => {
      state.selectedActionId = action.payload;
    },
    setExcelValidationError: (state, action: PayloadAction<boolean>) => {
      state.hasExcelValidationError = action.payload;
    },
    setMSRAttachmentValidationError: (state, action: PayloadAction<boolean>) => {
      state.hasMSRAttachmentValidationError = action.payload;
    },
    setSplitScreenMaximized: (state, action: PayloadAction<boolean>) => {
      state.isMaximized = action.payload;
    },
    setIsSplitScreen: (state, action: PayloadAction<boolean>) => {
      state.isSplitScreen = action.payload;
    },
    setWorkScopeItem: (state, action: PayloadAction<IDropdownOption[]>) => {
      state.workScopeItems = action.payload;
    },
    deleteTask: (state, action: PayloadAction<number>) => {
      state.tasksInformation[action.payload].TaskFlagIsActive = false;
      state.isTaskInformationDirty = true;
    },
    addTask: (state, action: PayloadAction<GPSTask>) => {
      state.tasksInformation = [...state.tasksInformation, action.payload];
      state.isTaskInformationDirty = true;
    },
    updateTask: (state, action: PayloadAction<{ index: number; data: GPSTask }>) => {
      state.tasksInformation[action.payload.index] = action.payload.data;
      state.isTaskInformationDirty = true;
    },
    //RBAC store data
    getRBACFeatures: (state, action: PayloadAction<string[]>) => {
      state.rbacFeatures = action.payload;
    },
    setIsAuthorize: (state, action: PayloadAction<boolean>) => {
      state.isAuthorized = action.payload;
    },
    setIsSplitView: (state, action: PayloadAction<boolean>) => {
      state.isSplitView = action.payload;
    },
    updateActionWorkItem: (state, action: PayloadAction<IWorkItem>) => {
      state.actionDetails = action.payload;
    },
    updateDeDupeLoading: (state, action: PayloadAction<string>) => {
      state.deDupeDataLoading = action.payload;
    },
    initActionGSIDetails: (state, action: PayloadAction<GSIDetails>) => {
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          gsiData: _.cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gsiData: _.cloneDeep(action.payload),
        },
      };
    },
    updateActionGSIDetails: (state, action: PayloadAction<GSIDetails>) => {
      const reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gsiData: action.payload,
        },
      };
      const isFormDirty = !_.isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
  },
  extraReducers: (builder) => {
    // Call back attached to get action item api
    builder.addMatcher(getActionDataApi.endpoints.getActionData.matchPending, (state) => {
      state.isGetActionCallInProgress = true;
    });
    builder.addMatcher(getActionDataApi.endpoints.getActionData.matchFulfilled, (state, action) => {
      state.actionDetails = action.payload;
      // TODO: Need to check if this is required & if yes, then have to update the logic to use ManageActionRedirection.tsx
      //This is commented out in premaster branch
      // redirectionGuide(action.payload?.Fields[MANAGE_ACTION_FIELDS.NodeName] || '');
      state.isGetActionCallInProgress = false;
    });
    builder.addMatcher(getActionDataApi.endpoints.getActionData.matchRejected, (state) => {
      state.isGetActionCallInProgress = false;
    });
    // Call back attached to get history api
    builder.addMatcher(getActionDataApi.endpoints.getHistory.matchPending, (state) => {
      state.isHistoryLoading = true;
    });
    builder.addMatcher(getActionDataApi.endpoints.getHistory.matchFulfilled, (state, action) => {
      state.history = (action.payload ? [...action.payload] : []).sort((a, b) => {
        return b.id - a.id;
      });
      state.isHistoryLoading = false;
    });
    builder.addMatcher(getActionDataApi.endpoints.getHistory.matchRejected, (state) => {
      state.isHistoryLoading = false;
    });
    builder.addMatcher(
      getActionDataApi.endpoints.getFollowing.matchFulfilled,
      (state, action: any) => {
        state.workItemSubscriptions = action.payload === '0000' ? false : true;
      }
    );
    builder.addMatcher(getActionDataApi.endpoints.addFollowing.matchFulfilled, (state) => {
      state.workItemSubscriptions = true;
      state.isFollowing = true;
    });
    builder.addMatcher(getActionDataApi.endpoints.deleteFollowing.matchFulfilled, (state) => {
      state.workItemSubscriptions = false;
      state.isFollowing = false;
    });
    builder.addMatcher(
      getActionDataApi.endpoints.getRelatedFeedbackItems.matchFulfilled,
      (state, action) => {
        state.technicalFeedbackList = action.payload?.RelatedItemFields;
      }
    );
    builder.addMatcher(
      getActionDataApi.endpoints.getWorkItemComments.matchFulfilled,
      (state, action) => {
        state.commentList = action.payload;
      }
    );
    //Initial load action stopped as it is not required on 8/8 release
    // builder.addMatcher(getActionDataApi.endpoints.getADOTags.matchFulfilled, (state, action) => {
    //   state.adoTags = action.payload;
    // });
    builder.addMatcher(rtkApi.endpoints.PersonDetails.matchFulfilled, (state, action) => {
      state.personalDetails = action.payload;
    });
    builder.addMatcher(rtkApi.endpoints.ManagersDetails.matchFulfilled, (state, action) => {
      state.managerDetails = action.payload;
    });
    builder.addMatcher(rtkApi.endpoints.GetAlias.matchFulfilled, (state, action) => {
      state.getAlias = action.payload;
    });
    builder.addMatcher(getActionDataApi.endpoints.GetEmail.matchFulfilled, (state, action) => {
      state.getEmail = action.payload;
    });
    builder.addMatcher(getActionDataApi.endpoints.GetByOpportunity.matchPending, (state) => {
      state.isFetchingOpportunityDetails = true;
    });
    builder.addMatcher(
      getActionDataApi.endpoints.GetPartnerEngagementByReferralID.matchPending,
      (state) => {
        state.isFetchingOpportunityDetails = true;
      }
    );
    builder.addMatcher(getActionDataApi.endpoints.GetCustomerByTPID.matchPending, (state) => {
      state.isFetchingOpportunityDetails = true;
    });
    builder.addMatcher(getActionDataApi.endpoints.GetByOpportunity.matchRejected, (state) => {
      state.isFetchingOpportunityDetails = false;
    });
    builder.addMatcher(
      getActionDataApi.endpoints.GetPartnerEngagementByReferralID.matchRejected,
      (state) => {
        state.isFetchingOpportunityDetails = false;
      }
    );
    builder.addMatcher(getActionDataApi.endpoints.GetCustomerByTPID.matchRejected, (state) => {
      state.isFetchingOpportunityDetails = false;
    });

    builder.addMatcher(
      getActionDataApi.endpoints.GetByOpportunity.matchFulfilled,
      (state, action) => {
        state.opportunityDetailData = {
          opportunityID: action.payload.msP_OpportunityNumber,
          referral: [],
          tpid: action.payload.customer.msX_TPId,
        };
        state.opportunityDetails = action.payload;
        state.isFetchingOpportunityDetails = false;
      }
    );
    builder.addMatcher(
      getActionDataApi.endpoints.GetPartnerEngagementByReferralID.matchFulfilled,
      (state, action) => {
        state.opportunityDetailData = {
          opportunityID: action?.payload?.[0].opportunity?.msP_OpportunityNumber,
          referral: [action?.payload?.[0].msp_partnercenterreferralid],
          tpid: action?.payload?.[0].account?.msP_MSTopParentId,
        };
        state.referralDetails = action?.payload;
        state.isFetchingOpportunityDetails = false;
      }
    );
    builder.addMatcher(
      getActionDataApi.endpoints.GetCustomerByTPID.matchFulfilled,
      (state, action) => {
        state.opportunityDetailData = {
          opportunityID: '',
          referral: [],
          tpid: action.payload.msX_TPId,
        };
        state.tpidDetails = action.payload;
        state.isFetchingOpportunityDetails = false;
      }
    );
    // Call back attached to update action item api
    builder.addMatcher(getActionDataApi.endpoints.updateActionItem.matchPending, (state) => {
      state.isUpdateActionCallInProgress = true;
    });
    builder.addMatcher(getActionDataApi.endpoints.updateActionItem.matchFulfilled, (state) => {
      state.isUpdateActionCallInProgress = false;
      state.UpdatedActionData.comments = '';
    });
    builder.addMatcher(
      getActionDataApi.endpoints.updateActionItem.matchRejected,
      (state, action) => {
        state.isUpdateActionCallInProgress = false;
        if (action?.payload?.status === HttpStatusCode.conflict) {
          state.isConflictDialogHidden = false;
        }
      }
    );
    builder.addMatcher(
      getActionDataApi.endpoints.GetPartnerRegionAreaFromCountry.matchFulfilled,
      (state, action) => {
        state.partnerRegionAreaFromCountry = action.payload as IPartnerRegionAreaFromCountry[];
      }
    );
    // Call back attached to update action item api
    builder.addMatcher(
      getActionDataApi.endpoints.getTasksInformation.matchFulfilled,
      (state, action) => {
        state.isTaskInformationDirty = false;
        state.tasksInformation = action.payload;
      }
    );
    builder.addMatcher(getActionDataApi.endpoints.saveTasksInformation.matchFulfilled, (state) => {
      state.isTaskInformationDirty = false;
    });
    builder.addMatcher(getActionDataApi.endpoints.getDeDupeData.matchFulfilled, (state, action) => {
      state.deDupeData = action.payload;
      state.deDupeDataLoading = false;
    });
  },
});

//Utility functions to get difference between two objects
export const difference = (origObj: any, newObj: any) => {
  const changes = (newObj: any, origObj: any) => {
    let arrayIndexCounter = 0;
    return _.transform(newObj, function (result: any, value: any, key: any) {
      if (!_.isEqual(value, origObj[key])) {
        const resultKey = _.isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          _.isObject(value) && _.isObject(origObj[key]) ? changes(value, origObj[key]) : value;
      }
    });
  };
  return changes(newObj, origObj);
};

export const printDifference = (reduxState: IGetActionDataSlice) => {
  console.log('difference', difference(reduxState.InitActionData, reduxState.UpdatedActionData));
};

export default manageActionSlice.reducer;

export const {
  initFormDirty,
  commitUpdatedActionData,
  initComments,
  initActionRelatedDetails,
  initRaidDetails,
  initActionCSGDetails,
  initActionVsuQbcDetails,
  initActionIATDetails,
  initActionMaccDetails,
  initActionRedButtonDetails,
  initActionGTTDetails,
  initActionCoSellDetails,
  initActionPMYTDetails,
  initActionGlobalTeamDetails,
  initActionCaeDetails,
  initActionCAPSLDetails,
  initActionAPEXDetails,
  initActionAOAIDetails,
  initActionECIFDetails,
  initActionGPSInvDetails,
  initActionBasicDetails,
  initActionAHPMDetails,
  initActionActionNominationsDetails,
  initActionAttachmentDetails,
  updateActionAttachmentDetails,
  setMSRAttachmentValidationError,
  initActionTagsDetails,
  initActionCompeteDetails,
  initActionLeftTabDetails,
  initActionMAASDetails,
  selectedAssignedTeam,
  updateActionRelatedDetails,
  updateActionBasicDetails,
  updateActionLeftTabDetails,
  getWorkItemComments,
  updateActionTagsDetails,
  updateActionNewCommentDetails,
  updateActionAOAIDetails,
  updateActionCosellDetails,
  updateActionGPSDetails,
  commonSharedDataDetails,
  PersonDetails,
  GetAlias,
  GetEmail,
  updateOpportunityDetails,
  setIsConflictDialogHidden,
  setIsFetchingOpportunityDetails,
  setUpdateActionApiStatus,
  setDWRRequiredFields,
  setCurrentRemoteUrl,
  setEditableFeatureList,
  setClickableStage,
  setSelectedActionId,
  setExcelValidationError,
  setSplitScreenMaximized,
  deleteTask,
  addTask,
  updateTask,
  getRBACFeatures,
  updateActionPMYTDetails,
  setIsAuthorize,
  setIsSplitView,
  updateActionWorkItem,
  setIsSplitScreen,
  updateDeDupeLoading,
  setWorkScopeItem,
  refreshPermissions,
  initActionGSIDetails,
  updateActionGSIDetails,
} = manageActionSlice.actions;

// Generated by GitHub Copilot | Updated on: 2026-10-07 | 5 lines modified | Committed saved action data as dirty baseline
// Copilot change tracking: 2026-11-01 migration: Retain frozen release reconciliation.
// Copilot change tracking: 2026-11-02 frontend: Remove the unused form-field constants import.
